import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type TagRuleType = 'required' | 'excluded' | 'preferred';

export interface PackageTagRule {
  tag_id: number;
  rule_type: TagRuleType;
  is_default: boolean;
  tag_name: string;
  tag_category: string;
}

export interface SubscriptionPackage {
  id: number;
  package_code: string;
  name: string;
  description: string | null;
  price_weekly: number | null;
  price_monthly: number | null;
  price_one_time: number | null;
  billing_model: 'subscription' | 'one_time';
  access_duration_days: number | null;
  has_track_restrictions: boolean;
  allow_multiple_subscriptions: boolean;
  display_order: number | null;
  is_active: boolean;
  is_private: boolean;
  is_default_for_all_users: boolean;
  is_promo_package: boolean;
  is_trial: boolean;
  auto_assign_on_registration: boolean;
  /** UTC ISO instant; not listed or buyable before it. null = no bound. */
  sale_starts_at: string | null;
  /** UTC ISO instant; off sale from it (is_active unchanged, buyers keep access). null = no bound. */
  sale_ends_at: string | null;
  /** 'YYYY-MM-DD': fixed last day of access for a one_time package. null = access_duration_days. */
  access_ends_on: string | null;
  active_subscriptions: number;
  created_at: string;
  updated_at: string;
  tag_rules: PackageTagRule[];
}

export function useSubscriptionPackages(includeInactive = false) {
  return useQuery({
    queryKey: ['subscription-packages', { includeInactive }],
    queryFn: () => apiClient<SubscriptionPackage[]>(`/subscription-packages?includeInactive=${includeInactive}`),
  });
}

function useInvalidateSubscriptionPackages() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['subscription-packages'] });
}

export type SubscriptionPackageDraft = Partial<SubscriptionPackage>;

export function useCreateSubscriptionPackage() {
  const invalidate = useInvalidateSubscriptionPackages();
  return useMutation({
    mutationFn: (draft: SubscriptionPackageDraft) =>
      apiClient<SubscriptionPackage>('/subscription-packages', { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useUpdateSubscriptionPackage() {
  const invalidate = useInvalidateSubscriptionPackages();
  return useMutation({
    mutationFn: ({ id, ...fields }: SubscriptionPackageDraft & { id: number }) =>
      apiClient<SubscriptionPackage>(`/subscription-packages/${id}`, { method: 'PUT', body: JSON.stringify(fields) }),
    onSuccess: invalidate,
  });
}

export function useDeleteSubscriptionPackage() {
  const invalidate = useInvalidateSubscriptionPackages();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ message: string }>(`/subscription-packages/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
